import { hasActiveReindex, hasRunningReindex } from "@/lib/documents/reindex";
import { documentIndexLayers, type DocumentIndexLayer } from "@/lib/ui/document-index";
import {
  documentIndexStageTimings,
  type IndexJobTimingInput,
  type IndexStageTimings,
} from "@/lib/ui/document-index-stage-timing";
import {
  documentIndexProgress,
  type DocumentIndexProgressView,
} from "@/lib/ui/document-progress";

export type DocumentIndexLive = {
  status: string;
  queued: boolean;
  index: DocumentIndexLayer[];
  progress: DocumentIndexProgressView | null;
  indexStage: string | null;
  indexStageAt: string | null;
  timings: IndexStageTimings;
};

export type DocumentIndexLiveRow = DocumentIndexLive & { id: string };

export function documentIndexLive(input: {
  status: string;
  parseStatus: string;
  vectorIndexedAt: Date | string | null;
  extractStatus: string;
  extractError?: string | null;
  indexStage: string | null;
  indexDone: number;
  indexTotal: number;
  indexStageAt: Date | string | null;
  indexBeatAt?: Date | string | null;
  jobStatuses: string[];
  jobs?: IndexJobTimingInput[];
  timeZone?: string;
}): DocumentIndexLive {
  const stageAt = input.indexStageAt
    ? new Date(input.indexStageAt).toISOString()
    : null;
  return {
    status: input.status,
    queued: hasActiveReindex(input.jobStatuses),
    index: documentIndexLayers({
      parseStatus: input.parseStatus,
      vectorIndexedAt: input.vectorIndexedAt,
      extractStatus: input.extractStatus,
      extractError: input.extractError,
      indexStage: input.indexStage,
    }),
    progress: documentIndexProgress({
      status: input.status,
      parseStatus: input.parseStatus,
      indexStage: input.indexStage,
      indexDone: input.indexDone,
      indexTotal: input.indexTotal,
      indexStageAt: input.indexStageAt,
      indexBeatAt: input.indexBeatAt,
      jobRunning: hasRunningReindex(input.jobStatuses),
    }),
    indexStage: input.indexStage,
    indexStageAt: stageAt,
    timings: documentIndexStageTimings({
      jobs: input.jobs ?? [],
      parseStatus: input.parseStatus,
      vectorIndexedAt: input.vectorIndexedAt,
      extractStatus: input.extractStatus,
      timeZone: input.timeZone,
    }),
  };
}

export function applyDocumentIndexLive<T extends { id: string }>(
  rows: T[],
  live: DocumentIndexLiveRow[],
): Array<T & DocumentIndexLive> {
  if (live.length === 0) {
    return rows as Array<T & DocumentIndexLive>;
  }
  const byId = new Map(live.map((item) => [item.id, item]));
  return rows.map((row) => {
    const next = byId.get(row.id);
    return (next ? { ...row, ...next } : row) as T & DocumentIndexLive;
  });
}

export function documentIndexLiveWatch(row: {
  status: string;
  queued: boolean;
  progress: DocumentIndexProgressView | null;
  index: DocumentIndexLayer[];
}): boolean {
  if (row.status === "PROCESSING" || row.queued || row.progress) {
    return true;
  }
  return row.index.some((layer) => layer.tone === "busy");
}
